/**
 * Self-test suite for authenticated quote/reply previews.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/QuotedReplySelfTest.swift` case-for-case,
 * split into three groups:
 *
 *   A. Codec — (de)serialisation, sentinel non-collision, schema validation,
 *      preview truncation:
 *      A1. encode → decode round-trips the whole payload
 *      A2. isReply detects our prefix and rejects chat / envelope /
 *          receipt / tombstone
 *      A3. tryDecode returns null for a plain user message
 *      A4. malformed base64 in the reply prefix throws MALFORMED
 *      A5. wrong schema version throws UNSUPPORTED_SCHEMA
 *      A6. empty quotedMessageId throws MALFORMED
 *      A7. preview truncation: at exact maxLength returns input;
 *          at maxLength+1 returns truncated input + ellipsis
 *
 *   B. Hash + verifier — authenticity primitives:
 *      B1. computeHash is deterministic and 32 bytes
 *      B2. computeHash differs for a one-character-changed input
 *      B3. verify returns .verified when local matches
 *      B4. verify returns .hashMismatch when local plaintext was edited
 *      B5. verify returns .authorMismatch when local author differs
 *      B6. verify returns .unverified when localOriginal is null
 *
 *   C. Server-forgery resistance:
 *      C1. attacker claims author "bob" for a message authored by "carol"
 *          → authorMismatch(claimed:"bob", actual:"carol")
 *      C2. attacker supplies a hash that does not match local plaintext
 *          → hashMismatch (even though author lines up)
 *      C3. .verified requires BOTH author and hash to match — neither
 *          alone is sufficient
 *
 *   D. Canonical bytes vector:
 *      D1. known (messageId, plaintext, authorId) → deterministic hash
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by `selftest/index.ts` via `import.meta.glob` (matches
 * `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  QuotedReplyCodec,
  QuotedReplyCodecError,
  type QuotedReplyPayload,
} from "./QuotedReply";
import { QuotedReplyVerifier } from "./QuotedReplyVerifier";

// ---------------------------------------------------------------------------
// A. Codec
// ---------------------------------------------------------------------------

/** A1. encode → decode round-trips all fields. */
async function testCodecRoundTrip(): Promise<TestCase> {
  const name = "codec round-trips quoted-reply payload";
  try {
    const body = "the original message Bob sent";
    const hash = await QuotedReplyCodec.computeHash(body);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "msg-1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1_700_000_000,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "I disagree.",
    };
    const encoded = await QuotedReplyCodec.encode(payload);
    const decoded = QuotedReplyCodec.decode(encoded);
    if (
      decoded.schema !== payload.schema ||
      decoded.quotedMessageId !== payload.quotedMessageId ||
      decoded.quotedAuthorId !== payload.quotedAuthorId ||
      decoded.quotedPlaintextHash !== payload.quotedPlaintextHash ||
      decoded.quotedTs !== payload.quotedTs ||
      decoded.quotedPreview !== payload.quotedPreview ||
      decoded.replyBody !== payload.replyBody
    ) {
      return fail(name, `decoded payload did not match original: ${JSON.stringify(decoded)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** A2. isReply distinguishes from chat / envelope / receipt / tombstone. */
async function testIsReplyDetection(): Promise<TestCase> {
  const name = "isReply distinguishes from chat/envelope/receipt/tombstone";
  try {
    const body = "hi";
    const hash = await QuotedReplyCodec.computeHash(body);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m",
      quotedAuthorId: "a",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: body,
      replyBody: "ok",
    };
    const encoded = await QuotedReplyCodec.encode(payload);
    const positive = QuotedReplyCodec.isReply(encoded);
    const negChat = QuotedReplyCodec.isReply("just a chat message");
    const negEnv = QuotedReplyCodec.isReply("kaether:e2ee:v1:foo");
    const negRcpt = QuotedReplyCodec.isReply("kaether:receipt:v1:abc");
    const negTomb = QuotedReplyCodec.isReply("kaether:tombstone:v1:abc");
    const ok = positive && !negChat && !negEnv && !negRcpt && !negTomb;
    if (!ok) {
      return fail(
        name,
        `pos=${positive} chat=${negChat} env=${negEnv} rcpt=${negRcpt} tomb=${negTomb}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** A3. tryDecode returns null for a plain user message. */
function testTryDecodeReturnsNullForUserMessage(): TestCase {
  const name = "tryDecode returns null for user messages";
  try {
    const result = QuotedReplyCodec.tryDecode("just a chat message");
    if (result !== null) {
      return fail(name, `expected null, got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

/** A4. Malformed base64 after the prefix throws MALFORMED. */
function testMalformedBase64Rejected(): TestCase {
  const name = "malformed base64 in quoted-reply is rejected";
  const bad = QuotedReplyCodec.prefix + "@@@not-base64@@@";
  try {
    QuotedReplyCodec.decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof QuotedReplyCodecError && e.code === "MALFORMED") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

/** A5. Wrong schema version throws UNSUPPORTED_SCHEMA. */
function testUnsupportedSchemaRejected(): TestCase {
  const name = "unsupported schema version is rejected";
  // 32 bytes of 0x11 in base64 = exactly 44 chars with padding.
  const hashB64 = btoa(String.fromCharCode(...new Array(32).fill(0x11)));
  const json = JSON.stringify({
    schema: 99,
    quotedMessageId: "abc",
    quotedAuthorId: "alice",
    quotedPlaintextHash: hashB64,
    quotedTs: 1,
    quotedPreview: "x",
    replyBody: "y",
  });
  const payload = QuotedReplyCodec.prefix + strToBase64url(json);
  try {
    QuotedReplyCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof QuotedReplyCodecError && e.code === "UNSUPPORTED_SCHEMA") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

/** A6. Empty quotedMessageId throws MALFORMED. */
function testEmptyQuotedMessageIdRejected(): TestCase {
  const name = "empty quotedMessageId is rejected";
  const hashB64 = btoa(String.fromCharCode(...new Array(32).fill(0x11)));
  const json = JSON.stringify({
    schema: 1,
    quotedMessageId: "",
    quotedAuthorId: "alice",
    quotedPlaintextHash: hashB64,
    quotedTs: 1,
    quotedPreview: "x",
    replyBody: "y",
  });
  const payload = QuotedReplyCodec.prefix + strToBase64url(json);
  try {
    QuotedReplyCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof QuotedReplyCodecError && e.code === "MALFORMED") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

/** A7. preview truncation: at maxLength returns input; at maxLength+1 returns cut + ellipsis. */
function testPreviewTruncationBoundary(): TestCase {
  const name = "preview truncation: <=max returns input, >max truncates + ellipsis";
  const exact = "a".repeat(QuotedReplyCodec.maxPreviewLength);
  const oneOver = exact + "b";
  const exactPreview = QuotedReplyCodec.preview(exact);
  const overPreview = QuotedReplyCodec.preview(oneOver);
  const okExact = exactPreview === exact;
  const expectedOver = exact + "…";
  const okOver = overPreview === expectedOver;
  if (!okExact || !okOver) {
    return fail(
      name,
      `exact ok=${okExact} over ok=${okOver} overPreview=${overPreview}`,
    );
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// B. Hash + verifier
// ---------------------------------------------------------------------------

/** B1. computeHash is deterministic and 32 bytes. */
async function testComputeHashDeterministic(): Promise<TestCase> {
  const name = "computeHash is deterministic and 32 bytes";
  try {
    const body = "Bob's original message — with unicode é";
    const h1 = await QuotedReplyCodec.computeHash(body);
    const h2 = await QuotedReplyCodec.computeHash(body);
    // standard base64 for 32 bytes = 44 chars (with padding).
    const h1Bytes = base64ToUint8Array(h1);
    const ok = h1 === h2 && h1Bytes.byteLength === 32;
    if (!ok) {
      return fail(name, `h1=${h1} h2=${h2} equal=${h1 === h2} bytes=${h1Bytes.byteLength}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** B2. computeHash differs for a one-character-changed input. */
async function testComputeHashSensitiveToChange(): Promise<TestCase> {
  const name = "computeHash differs for one-character-changed input";
  try {
    const h1 = await QuotedReplyCodec.computeHash("hello world");
    const h2 = await QuotedReplyCodec.computeHash("hello worle"); // last char changed
    if (h1 === h2) {
      return fail(name, "hashes collided");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** B3. verify returns verified when local matches. */
async function testVerifyVerifiedOnMatch(): Promise<TestCase> {
  const name = "verify returns verified when author + hash match";
  try {
    const body = "the original from bob";
    const hash = await QuotedReplyCodec.computeHash(body);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "ok",
    };
    const result = await QuotedReplyVerifier.verify(payload, {
      authorId: "bob",
      plaintext: body,
    });
    if (result.kind !== "verified" || result.localPlaintext !== body) {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** B4. verify returns hashMismatch when local plaintext was edited. */
async function testVerifyHashMismatchOnEditedBody(): Promise<TestCase> {
  const name = "verify returns hashMismatch when local body differs";
  try {
    const originalAtSendTime = "Bob said this";
    const edited = "Bob said something else"; // attacker swapped / user edited
    const hash = await QuotedReplyCodec.computeHash(originalAtSendTime);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(originalAtSendTime),
      replyBody: "yes",
    };
    const result = await QuotedReplyVerifier.verify(payload, {
      authorId: "bob",
      plaintext: edited,
    });
    if (result.kind !== "hashMismatch") {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** B5. verify returns authorMismatch when local author differs. */
async function testVerifyAuthorMismatch(): Promise<TestCase> {
  const name = "verify returns authorMismatch when local author differs";
  try {
    const body = "shared body";
    const hash = await QuotedReplyCodec.computeHash(body);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "huh",
    };
    const result = await QuotedReplyVerifier.verify(payload, {
      authorId: "carol",
      plaintext: body,
    });
    if (
      result.kind !== "authorMismatch" ||
      result.claimed !== "bob" ||
      result.actual !== "carol"
    ) {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** B6. verify returns unverified when localOriginal is null. */
async function testVerifyUnverifiedWhenNoLocalCopy(): Promise<TestCase> {
  const name = "verify returns unverified when no local original";
  try {
    const body = "we don't have this locally";
    const hash = await QuotedReplyCodec.computeHash(body);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "noted",
    };
    const result = await QuotedReplyVerifier.verify(payload, null);
    if (result.kind !== "unverified") {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// C. Server-forgery resistance
// ---------------------------------------------------------------------------

/**
 * C1. Attacker claims author "bob" for a message locally authored by "carol"
 * → authorMismatch (even though hash incidentally matches).
 */
async function testAttackerLiesAboutAuthor(): Promise<TestCase> {
  const name = "hostile sender's author claim is caught";
  try {
    const actualBody = "what carol actually said";
    const hash = await QuotedReplyCodec.computeHash(actualBody);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob", // lie
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(actualBody),
      replyBody: "interesting",
    };
    const result = await QuotedReplyVerifier.verify(payload, {
      authorId: "carol",
      plaintext: actualBody,
    });
    if (
      result.kind !== "authorMismatch" ||
      result.claimed !== "bob" ||
      result.actual !== "carol"
    ) {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * C2. Attacker author lines up but wire hash doesn't match local plaintext
 * → hashMismatch.
 */
async function testAttackerLiesAboutHash(): Promise<TestCase> {
  const name = "hostile sender's hash claim is caught";
  try {
    const actualBody = "bob's real message";
    const liedAboutBody = "bob said something completely different";
    const lieHash = await QuotedReplyCodec.computeHash(liedAboutBody);
    const payload: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: lieHash, // computed from wrong body
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(liedAboutBody),
      replyBody: "noted",
    };
    const result = await QuotedReplyVerifier.verify(payload, {
      authorId: "bob",
      plaintext: actualBody,
    });
    if (result.kind !== "hashMismatch") {
      return fail(name, `got ${JSON.stringify(result)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * C3. verified requires BOTH author and hash to match — neither alone is
 * sufficient. Three sub-cases: hash-right/author-wrong, author-right/hash-wrong,
 * and both-right.
 */
async function testVerifiedRequiresBothAuthorAndHash(): Promise<TestCase> {
  const name = ".verified requires BOTH author and hash to match";
  try {
    const body = "shared text";
    const hash = await QuotedReplyCodec.computeHash(body);
    const wrongHash = await QuotedReplyCodec.computeHash("different");

    // Case 1: hash right, author wrong → not verified.
    const p1: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "r",
    };
    const r1 = await QuotedReplyVerifier.verify(p1, {
      authorId: "carol",
      plaintext: body,
    });
    const r1NotVerified = r1.kind !== "verified";

    // Case 2: author right, hash wrong → not verified.
    const p2: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: wrongHash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "r",
    };
    const r2 = await QuotedReplyVerifier.verify(p2, {
      authorId: "bob",
      plaintext: body,
    });
    const r2NotVerified = r2.kind !== "verified";

    // Case 3: both right → verified.
    const p3: QuotedReplyPayload = {
      schema: QuotedReplyCodec.currentSchema,
      quotedMessageId: "m1",
      quotedAuthorId: "bob",
      quotedPlaintextHash: hash,
      quotedTs: 1,
      quotedPreview: QuotedReplyCodec.preview(body),
      replyBody: "r",
    };
    const r3 = await QuotedReplyVerifier.verify(p3, {
      authorId: "bob",
      plaintext: body,
    });
    const r3Verified = r3.kind === "verified";

    const ok = r1NotVerified && r2NotVerified && r3Verified;
    if (!ok) {
      return fail(
        name,
        `r1=${JSON.stringify(r1)} r2=${JSON.stringify(r2)} r3=${JSON.stringify(r3)}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// D. Canonical bytes vector
// ---------------------------------------------------------------------------

/**
 * D1. Known (messageId, plaintext, authorId) → deterministic hash matches
 * a precomputed value.
 *
 * SHA256("the original from bob") in base64 =
 *   "DGhekeXEOy5mee0QoIeSMDYHeZ/OpqXg9Uxrp4I/9tY="
 *
 * This is the same value produced by iOS's `QuotedReplyCodec.computeHash`
 * on the same input string (UTF-8 bytes, SHA256, standard base64 encoding).
 */
async function testCanonicalBytesVector(): Promise<TestCase> {
  const name = "canonical bytes: known input produces deterministic hash";
  try {
    const plaintext = "the original from bob";
    // SHA256("the original from bob") — precomputed reference value.
    const expected = "DGhekeXEOy5mee0QoIeSMDYHeZ/OpqXg9Uxrp4I/9tY=";
    const actual = await QuotedReplyCodec.computeHash(plaintext);
    if (actual !== expected) {
      return fail(name, `expected ${expected}, got ${actual}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Helpers (local to the suite — no private symbol imports)
// ---------------------------------------------------------------------------

/** Encode a string to base64url without padding. */
function strToBase64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

/** Decode standard base64 to Uint8Array. */
function base64ToUint8Array(s: string): Uint8Array {
  const padLen = (4 - (s.length % 4)) % 4;
  const b64 = s + "=".repeat(padLen);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Quote/reply (authenticated)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Codec
        testCodecRoundTrip(),
        testIsReplyDetection(),
        Promise.resolve(testTryDecodeReturnsNullForUserMessage()),
        Promise.resolve(testMalformedBase64Rejected()),
        Promise.resolve(testUnsupportedSchemaRejected()),
        Promise.resolve(testEmptyQuotedMessageIdRejected()),
        Promise.resolve(testPreviewTruncationBoundary()),
        // B. Hash + verifier
        testComputeHashDeterministic(),
        testComputeHashSensitiveToChange(),
        testVerifyVerifiedOnMatch(),
        testVerifyHashMismatchOnEditedBody(),
        testVerifyAuthorMismatch(),
        testVerifyUnverifiedWhenNoLocalCopy(),
        // C. Server-forgery resistance
        testAttackerLiesAboutAuthor(),
        testAttackerLiesAboutHash(),
        testVerifiedRequiresBothAuthorAndHash(),
        // D. Canonical bytes
        testCanonicalBytesVector(),
      ]),
    ),
});
